import { uiElement, uiEvent } from "@kluntje/core";
import { FormField } from "Components/abstract/sde-e-form-field/sde-e-form-field";
import { CHANGED_EVENT } from "Constants/eventTypes";
import { renderTemplate } from "Helper/renderHelper/renderTemplate";

export class Textarea extends FormField<string> {
  @uiElement(".sde-e-form-field__input")
  private input: HTMLInputElement;

  private get minLength(): number | undefined {
    const curMinlength = this.getAttribute("minlength");
    if (curMinlength === null) return undefined;
    return parseInt(curMinlength, 10);
  }

  private get maxLength(): number | undefined {
    const curMaxlength = this.getAttribute("maxlength");
    if (curMaxlength === null) return undefined;
    return parseInt(curMaxlength, 10);
  }

  private get rowsCount(): number | undefined {
    const attrValue = this.getAttribute("rows");
    if (attrValue === null) return undefined;
    const parsedValue = parseInt(attrValue, 10);
    return isNaN(parsedValue) ? undefined : parsedValue;
  }

  private get initialValue(): string {
    return this.getAttribute("initial-value") || "";
  }

  public get submitValue(): string {
    return this.input?.value ?? "";
  }

  public get submitString(): string | undefined {
    return this.submitValue;
  }

  protected async initFormField() {
    if (this.initialValue !== "") {
      this.input.value = this.initialValue;
      await this.reRenderHook();
    }
    super.initFormField();
  }

  protected async renderAsync() {
    const { textareaTemplate } = await import("./sde-e-textarea.template");
    if (this.state.inValidationMode) await this.validate();

    renderTemplate(
      textareaTemplate({
        id: this.uuid,
        name: this.name,
        placeholder: this.placeholder,
        label: this.label,
        isRequired: this.isRequired,
        isReadonly: this.isReadonly,
        isDisabled: this.isDisabled,
        currentValue: this.submitValue,
        rowsCount: this.rowsCount,
        minLength: this.minLength,
        maxLength: this.maxLength,
        description: this.description,
        errorMessage: this.errorMessage,
        showError: this.state.showError,
      }),
      this.getUiRoot(),
    );
  }

  @uiEvent("input", "keyup")
  protected async handleKeyup() {
    await this.reRenderHook();
    this.dispatchEvent(new CustomEvent(CHANGED_EVENT, { bubbles: false }));
  }

  protected async handleFocusOut(): Promise<void> {
    await super.handleFocusOut();
    const currentValue = this.submitValue ?? "";
    const isValid = await this.isValid();
    if (currentValue === "" && isValid) return;
    await this.validate();
    await this.reRenderHook();
  }

  public async reset() {
    this.setState({ showError: false, inValidationMode: false });
    this.input.value = this.initialValue;
    await this.prefillInput();
  }
}

customElements.define("sde-e-textarea", Textarea);
